import React, { useState, useCallback, useEffect, useMemo } from "react";
import cloneDeep from "lodash/cloneDeep";
import Modal from "@cloudscape-design/components/modal";
import Select from "@cloudscape-design/components/select";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import Container from "@cloudscape-design/components/container";
import Grid from "@cloudscape-design/components/grid";
import Toggle from "@cloudscape-design/components/toggle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { playerRulesEditModalStore } from "../stores/playerRulesEditModal";
import { ColorPicker, useColor } from "react-color-palette";
import "react-color-palette/css";
import { userStore } from "../stores/access";
import Multiselect from "@cloudscape-design/components/multiselect";
import DatePicker from "@cloudscape-design/components/date-picker";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import TimeInput from "@cloudscape-design/components/time-input";

const conditionTypes = [
  { value: "SIMPLE", label: "Simple Condition" },
  { value: "DATE_SUB", label: "Date Subtract" },
  { value: "COUNT", label: "Count" },
  { value: "COUNT_DISTINCT", label: "Count distinct" },
  { value: "SUM", label: "Sum" },
  { value: "MINUS", label: "Minus (Compare Values)" },
  { value: "SAME", label: "Same (Compare with Others)" },
  { value: "RATIO", label: "Ratio (Divide Values)" },
];

const operators = [
  "=", ">", ">=", "<", "<=", "!=", "LIKE", "LIKE %...%", "NOT LIKE",
  "IN", "NOT IN", "BETWEEN", "NOT BETWEEN", "IS NULL", "IS NOT NULL",
  "REGEXP", "NOT REGEXP",
].map((e) => ({ label: e, value: e }));

const dateOperators = ["=", ">", ">=", "<", "<=", "!="].map((e) => ({
  label: e,
  value: e,
}));

const operations = [
  { value: "SUM", label: "Sum" },
  { value: "COUNT", label: "Count" },
  { value: "COUNT_DISTINCT", label: "Count distinct" },
];

const units = [
  { value: "DAY", label: "Day" },
  { value: "MONTH", label: "Month" },
  { value: "YEAR", label: "Year" },
  { value: "MINUTE", label: "Minute" },
  { value: "HOUR", label: "Hour" },
];

const logicalOperators = [
  { value: "AND", label: "AND" },
  { value: "OR", label: "OR" },
];

const resultFormats = [
  { value: "NUMBER", label: "Number (e.g., 0.8)" },
  { value: "PERCENTAGE", label: "Percentage (e.g., 80%)" },
];

const dateModes = [
  { value: "relative", label: "Relative period" },
  { value: "fixed", label: "Fixed date" },
  { value: "range", label: "Date range" },
];

const fixedDateGridDefinition = [
  { colspan: { default: 12, xs: 6, s: 3 } },
  { colspan: { default: 12, xs: 6, s: 3 } },
];

const dateRangeI18nStrings = {
  todayAriaLabel: "Today",
  nextMonthAriaLabel: "Next month",
  previousMonthAriaLabel: "Previous month",
  customRelativeRangeDurationLabel: "Duration",
  customRelativeRangeDurationPlaceholder: "Enter duration",
  customRelativeRangeOptionLabel: "Custom range",
  customRelativeRangeOptionDescription: "Define a custom range",
  customRelativeRangeUnitLabel: "Unit of time",
  formatRelativeRange: ({ startDate, endDate }) => `${new Date(startDate).toLocaleString()} – ${new Date(endDate).toLocaleString()}`,
  formatUnit: (unit, amount) => `${unit}${amount === 1 ? "" : "s"}`,
  dateTimeConstraintText: "Select the start and end date and time.",
  renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
  labelRelativeRange: "Relative range",
  labelAbsoluteRange: "Absolute range",
  dateTimePickerAriaLabel: "Date range picker",
  dateTimePickerLabel: "Date and time",
  clearButtonAriaLabel: "Clear date range",
  clearButtonLabel: "Clear range",
  relativeModeTitle: "Relative mode",
  absoluteModeTitle: "Absolute mode",
  cancelButtonLabel: "Cancel",
  applyButtonLabel: "Apply",
  startDateLabel: "Start date",
  startTimeLabel: "Start time",
  endDateLabel: "End date",
  endTimeLabel: "End time",
};

const getDateMode = (source) => {
  const mode = source?.params?.dateMode ?? source?.mode;
  return (typeof mode === "string" ? mode : mode?.value) || "relative";
};

const buildDateTimeValue = (date, time) => (date ? `${date}T${time || "00:00"}:00` : null);

const splitDateTimeValue = (value) => {
  if (!value) return { date: "", time: "" };
  const [date = "", rawTime = ""] = String(value).replace(" ", "T").split("T");
  return { date, time: rawTime.slice(0, 5) };
};

const createDateModeParams = () => ({
  dateMode: dateModes[0],
  fixedDate: "",
  fixedTime: "",
  rangeStartDate: "",
  rangeStartTime: "",
  rangeEndDate: "",
  rangeEndTime: "",
});

const applyDateModePayload = (target, source) => {
  const mode = getDateMode(source);
  target.mode = mode;

  if (mode === "relative") {
    target.unit = source.params?.unit?.value || source.unit || null;
    return target;
  }

  delete target.value;
  delete target.unit;

  if (mode === "fixed") {
    target.fixedDate = buildDateTimeValue(source.params?.fixedDate, source.params?.fixedTime);
    delete target.rangeStart;
    delete target.rangeEnd;
    return target;
  }

  delete target.operator;
  target.rangeStart = buildDateTimeValue(source.params?.rangeStartDate, source.params?.rangeStartTime);
  target.rangeEnd = buildDateTimeValue(source.params?.rangeEndDate, source.params?.rangeEndTime);
  return target;
};

const validateDateModeFields = ({ mode, operator, params }, errors) => {
  let isValid = true;

  if (!dateModes.some((option) => option.value === mode)) {
    errors.dateMode = "Select a date mode";
    isValid = false;
  }
  if (mode !== "range" && !operator) {
    errors.operator = "Select an operator";
    isValid = false;
  }
  if (mode === "relative") {
    if (!params.unit) {
      errors.unit = "Select a unit";
      isValid = false;
    }
    if (params.value === "" || params.value === null || params.value === undefined) {
      errors.value = "Enter a value";
      isValid = false;
    }
  } else if (mode === "fixed") {
    if (!params.fixedDate) {
      errors.fixedDate = "Select a date";
      isValid = false;
    }
  } else if (mode === "range") {
    const { rangeStartDate, rangeStartTime, rangeEndDate, rangeEndTime } = params;
    if (!rangeStartDate) {
      errors.rangeStart = "Select a start date and time";
      isValid = false;
    }
    if (!rangeEndDate) {
      errors.rangeEnd = "Select an end date and time";
      isValid = false;
    }
    if (rangeStartDate && rangeEndDate && buildDateTimeValue(rangeStartDate, rangeStartTime) > buildDateTimeValue(rangeEndDate, rangeEndTime)) {
      errors.rangeOrder = "The start must not be later than the end";
      isValid = false;
    }
  }

  return isValid;
};

const validateSubCondition = (subCondition, subErrors) => {
  let isValid = true;

  if (!subCondition.params.sourceFields[0]?.field) {
    subErrors.field = "Select a field";
    isValid = false;
  }

  if (subCondition.type === "DATE_CONDITIONAL") {
    if (!validateDateModeFields({ mode: getDateMode(subCondition), operator: subCondition.operator, params: subCondition.params }, subErrors)) {
      isValid = false;
    }
  } else {
    if (!subCondition.operator) {
      subErrors.operator = "Select an operator";
      isValid = false;
    }
    if (["IN", "NOT IN"].includes(subCondition.operator?.value) && (!subCondition.params.value || subCondition.params.value.length === 0)) {
      subErrors.value = `Add at least one value for ${subCondition.operator?.value}`;
      isValid = false;
    } else if (!["IN", "NOT IN"].includes(subCondition.operator?.value) && !subCondition.params.value) {
      subErrors.value = "Enter a value";
      isValid = false;
    }
  }

  if (!subCondition.params.logicalOperator) {
    subErrors.logicalOperator = "Select a logical operator";
    isValid = false;
  }

  return isValid;
};

const SubConditionForm = ({
                            conditionIndex,
                            sourceFieldIndex,
                            subCondition,
                            subIndex,
                            condition,
                            updateSubCondition,
                            updateSourceField,
                            updateParam,
                            removeSubCondition,
                            errors,
                            loading,
                            availableFields,
                          }) => {
  const dateMode = getDateMode(subCondition);

  return (
    <Container
      key={subIndex}
      header={<Box variant="h4">Sub-Condition {subIndex + 1} ({subCondition.type})</Box>}
      variant="stacked"
    >
      <SpaceBetween direction="vertical" size="s">
        <FormField label="Condition Type" stretch>
          <Select
            filteringType="auto"
            placeholder="Condition Type"
            selectedOption={subCondition.type ? { value: subCondition.type, label: subCondition.type } : null}
            onChange={({ detail }) =>
              updateSubCondition(conditionIndex, subIndex, "type", detail.selectedOption?.value, sourceFieldIndex)
            }
            options={["DATE_CONDITIONAL", "COMBINE_CONDITIONAL"].map((ct) => ({
              value: ct,
              label: ct,
            }))}
            disabled={loading}
          />
        </FormField>
        {subCondition.type === "DATE_CONDITIONAL" && (
          <>
            <FormField
              stretch
              label="Date Field"
              errorText={errors?.subConditions?.[subIndex]?.field}
            >
              <Select
                filteringType="auto"
                placeholder="Select a date field"
                selectedOption={subCondition.params.sourceFields[0]?.field || null}
                onChange={({ detail }) =>
                  updateSourceField(conditionIndex, sourceFieldIndex, "field", detail.selectedOption, subIndex)
                }
                options={availableFields.filter((e) => e.type?.includes("date") || e.value?.includes("date")) || []}
                disabled={loading}
              />
            </FormField>
            <FormField
              stretch
              label="Date mode"
              errorText={errors?.subConditions?.[subIndex]?.dateMode}
            >
              <Select
                filteringType="auto"
                placeholder="Select a date mode"
                selectedOption={dateModes.find((option) => option.value === dateMode) || dateModes[0]}
                onChange={({ detail }) =>
                  updateParam(conditionIndex, "dateMode", detail.selectedOption, subIndex, sourceFieldIndex)
                }
                options={dateModes}
                disabled={loading}
              />
            </FormField>
            {dateMode !== "range" && (
              <FormField
                stretch
                label="Operator"
                errorText={errors?.subConditions?.[subIndex]?.operator}
              >
                <Select
                  filteringType="auto"
                  placeholder="Select an operator"
                  selectedOption={subCondition.operator || null}
                  onChange={({ detail }) =>
                    updateSubCondition(conditionIndex, subIndex, "operator", detail.selectedOption, sourceFieldIndex)
                  }
                  options={dateOperators}
                  disabled={loading}
                />
              </FormField>
            )}
            {dateMode === "relative" && (
              <>
                <FormField
                  stretch
                  label="Unit"
                  errorText={errors?.subConditions?.[subIndex]?.unit}
                >
                  <Select
                    filteringType="auto"
                    placeholder="Select unit"
                    selectedOption={subCondition.params.unit || null}
                    onChange={({ detail }) =>
                      updateParam(conditionIndex, "unit", detail.selectedOption, subIndex, sourceFieldIndex)
                    }
                    options={units}
                    disabled={loading}
                  />
                </FormField>
                <FormField
                  stretch
                  label="Value"
                  errorText={errors?.subConditions?.[subIndex]?.value}
                >
                  <Input
                    type="number"
                    value={subCondition.params.value || ""}
                    onChange={({ detail }) =>
                      updateParam(conditionIndex, "value", detail.value, subIndex, sourceFieldIndex)
                    }
                    placeholder="Enter value (e.g., 1)"
                    disabled={loading || !subCondition.params.unit}
                    stretch
                  />
                </FormField>
              </>
            )}
            {dateMode === "fixed" && (
              <Grid gridDefinition={fixedDateGridDefinition}>
                <FormField stretch label="Date" errorText={errors?.subConditions?.[subIndex]?.fixedDate}>
                  <DatePicker
                    value={subCondition.params.fixedDate || ""}
                    onChange={({ detail }) => updateParam(conditionIndex, "fixedDate", detail.value, subIndex, sourceFieldIndex)}
                    placeholder="YYYY-MM-DD"
                    disabled={loading}
                  />
                </FormField>
                <FormField stretch label="Time" constraintText="Optional. Defaults to 00:00" errorText={errors?.subConditions?.[subIndex]?.fixedTime}>
                  <div style={{ maxWidth: "234px" }}>
                    <TimeInput
                      value={subCondition.params.fixedTime || ""}
                      onChange={({ detail }) => updateParam(conditionIndex, "fixedTime", detail.value, subIndex, sourceFieldIndex)}
                      format="hh:mm"
                      placeholder="hh:mm"
                      disabled={loading}
                    />
                  </div>
                </FormField>
              </Grid>
            )}
            {dateMode === "range" && (
              <FormField
                stretch
                label="Date range"
                errorText={errors?.subConditions?.[subIndex]?.rangeStart || errors?.subConditions?.[subIndex]?.rangeEnd || errors?.subConditions?.[subIndex]?.rangeOrder}
              >
                <DateRangePicker
                  value={
                    buildDateTimeValue(subCondition.params.rangeStartDate, subCondition.params.rangeStartTime) && buildDateTimeValue(subCondition.params.rangeEndDate, subCondition.params.rangeEndTime)
                      ? {
                        type: "absolute",
                        startDate: buildDateTimeValue(subCondition.params.rangeStartDate, subCondition.params.rangeStartTime),
                        endDate: buildDateTimeValue(subCondition.params.rangeEndDate, subCondition.params.rangeEndTime),
                      }
                      : null
                  }
                  onChange={({ detail }) => {
                    const start = splitDateTimeValue(detail.value?.startDate);
                    const end = splitDateTimeValue(detail.value?.endDate);
                    updateParam(conditionIndex, "rangeStartDate", start.date, subIndex, sourceFieldIndex);
                    updateParam(conditionIndex, "rangeStartTime", start.time, subIndex, sourceFieldIndex);
                    updateParam(conditionIndex, "rangeEndDate", end.date, subIndex, sourceFieldIndex);
                    updateParam(conditionIndex, "rangeEndTime", end.time, subIndex, sourceFieldIndex);
                  }}
                  dateOnly={false}
                  timeInputFormat="hh:mm"
                  rangeSelectorMode="absolute-only"
                  relativeOptions={[]}
                  showClearButton
                  isValidRange={(range) => {
                    if (!range?.startDate) return { valid: false, errorMessage: "Select a start date and time." };
                    if (!range?.endDate) return { valid: false, errorMessage: "Select an end date and time." };
                    if (new Date(range.startDate).getTime() > new Date(range.endDate).getTime()) return { valid: false, errorMessage: "The start must not be later than the end." };
                    return { valid: true };
                  }}
                  i18nStrings={dateRangeI18nStrings}
                  placeholder="Select start and end"
                  disabled={loading}
                />
              </FormField>
            )}
            <FormField stretch>
              <Toggle
                checked={subCondition.params.isUnixTimestamp || false}
                onChange={({ detail }) =>
                  updateParam(conditionIndex, "isUnixTimestamp", detail.checked, subIndex, sourceFieldIndex)
                }
                disabled={loading}
              >
                Unix Time
              </Toggle>
            </FormField>
            <FormField
              stretch
              label="Logical Operator"
              errorText={errors?.subConditions?.[subIndex]?.logicalOperator}
            >
              <Select
                filteringType="auto"
                placeholder="Select logical operator"
                selectedOption={subCondition.params.logicalOperator || null}
                onChange={({ detail }) =>
                  updateParam(conditionIndex, "logicalOperator", detail.selectedOption, subIndex, sourceFieldIndex)
                }
                options={logicalOperators}
                disabled={loading}
              />
            </FormField>
          </>
        )}
        {subCondition.type === "COMBINE_CONDITIONAL" && (
          <>
            <FormField
              stretch
              label="Field"
              errorText={errors?.subConditions?.[subIndex]?.field}
            >
              <Select
                filteringType="auto"
                placeholder="Select a field"
                selectedOption={subCondition.params.sourceFields[0]?.field || null}
                onChange={({ detail }) =>
                  updateSourceField(conditionIndex, sourceFieldIndex, "field", detail.selectedOption, subIndex)
                }
                options={availableFields.filter((e) => !e.type?.includes("date")) || []}
                disabled={loading}
              />
            </FormField>
            <FormField
              stretch
              label="Operator"
              errorText={errors?.subConditions?.[subIndex]?.operator}
            >
              <Select
                filteringType="auto"
                placeholder="Select an operator"
                selectedOption={subCondition.operator || null}
                onChange={({ detail }) =>
                  updateSubCondition(conditionIndex, subIndex, "operator", detail.selectedOption, sourceFieldIndex)
                }
                options={operators}
                disabled={loading}
              />
            </FormField>
            <FormField
              stretch
              label="Value"
              errorText={errors?.subConditions?.[subIndex]?.value}
            >
              {["IN","NOT IN"].includes(subCondition.operator?.value) ? (
                <SpaceBetween direction="horizontal" size="xs">
                  <Input
                    value={subCondition.inInputValue || ""}
                    onChange={({ detail }) =>
                      updateSubCondition(conditionIndex, subIndex, "inInputValue", detail.value, sourceFieldIndex)
                    }
                    placeholder="Enter value"
                    disabled={loading}
                    stretch
                  />
                  <Button
                    iconName="add-plus"
                    onClick={() => {
                      if (subCondition.inInputValue) {
                        const newValue = [
                          ...(subCondition.params.value || []),
                          { label: subCondition.inInputValue, value: subCondition.inInputValue },
                        ];
                        updateSubCondition(conditionIndex, subIndex, "value", newValue, sourceFieldIndex);
                        updateSubCondition(conditionIndex, subIndex, "inInputValue", "", sourceFieldIndex);
                      }
                    }}
                    disabled={loading || !subCondition.inInputValue}
                    variant="primary"
                    stretch
                  >
                    Add IN
                  </Button>
                  <Box>
                    {subCondition.params.value?.length > 0 ? (
                      subCondition.params.value.map((val, valIndex) => (
                        <Box
                          key={valIndex}
                          padding="xxs"
                          backgroundColor="grey-200"
                          borderRadius="small"
                          margin={{ right: "xs" }}
                          display="inline-block"
                        >
                          {val.value}
                          <Button
                            iconName="close"
                            variant="link"
                            onClick={() =>
                              updateSubCondition(
                                conditionIndex,
                                subIndex,
                                "value",
                                subCondition.params.value.filter((v) => v.value !== val.value),
                                sourceFieldIndex
                              )
                            }
                            disabled={loading}
                            stretch
                          />
                        </Box>
                      ))
                    ) : (
                      <Box color="text-status-info">No values added</Box>
                    )}
                  </Box>
                </SpaceBetween>
              ) : (
                <Input
                  value={subCondition.params.value || ""}
                  onChange={({ detail }) =>
                    updateSubCondition(conditionIndex, subIndex, "value", detail.value, sourceFieldIndex)
                  }
                  placeholder="Enter value"
                  disabled={loading || !subCondition.operator}
                  stretch
                />
              )}
            </FormField>
            <FormField
              stretch
              label="Logical Operator"
              errorText={errors?.subConditions?.[subIndex]?.logicalOperator}
            >
              <Select
                filteringType="auto"
                placeholder="Select logical operator"
                selectedOption={subCondition.params.logicalOperator || null}
                onChange={({ detail }) =>
                  updateParam(conditionIndex, "logicalOperator", detail.selectedOption, subIndex, sourceFieldIndex)
                }
                options={logicalOperators}
                disabled={loading}
              />
            </FormField>
          </>
        )}
        <Button
          iconName="remove"
          onClick={() => removeSubCondition(conditionIndex, subIndex, sourceFieldIndex)}
          disabled={loading}
          variant="link"
        >
          Remove Sub-Condition
        </Button>
        {errors?.subConditions?.[subIndex] && (
          <Box color="text-status-error">
            {Object.values(errors.subConditions[subIndex]).map(
              (error, errIndex) => (error && typeof error === "string" ? <div key={errIndex}>{error}</div> : null)
            )}
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

const ConditionForm = ({
                         condition,
                         index,
                         updateCondition,
                         updateSourceField,
                         updateParam,
                         removeCondition,
                         addSubCondition,
                         removeSubCondition,
                         updateSubCondition,
                         errors,
                         loading,
                         availableModules,
                         loadFields,
                       }) => {
  const formattedModules = Array.isArray(availableModules)
    ? availableModules.map((mod) => ({
      label: mod.label || mod.value || mod,
      value: mod.value || mod,
    }))
    : [];


  return (
    <Container header={<Box variant="h3">Condition {index + 1} ({condition.type})</Box>}>
      <SpaceBetween direction="vertical" size="s">
        {["MINUS", "RATIO", "SAME"].includes(condition.type) ? (
          <SpaceBetween direction="vertical" size="s">
            {condition.type === "SAME" && (
              <>
                <FormField
                  stretch
                  label="Module (Current Record)"
                  errorText={errors[index]?.module}
                  constraintText="Required"
                >
                  <Select
                    filteringType="auto"
                    placeholder="Select a module"
                    selectedOption={condition.module || null}
                    onChange={({ detail }) => {
                      updateCondition(index, "module", detail.selectedOption);
                      if (detail.selectedOption?.value) {
                        loadFields(detail.selectedOption, index);
                      }
                    }}
                    options={formattedModules}
                    disabled={loading}
                  />
                </FormField>
                <FormField
                  stretch
                  label="Field (Current Record)"
                  errorText={errors[index]?.field}
                  constraintText="Required"
                >
                  <Select
                    filteringType="auto"
                    placeholder="Select a field"
                    selectedOption={condition.field || null}
                    onChange={({ detail }) => updateCondition(index, "field", detail.selectedOption)}
                    options={condition.availableFields || []}
                    disabled={loading || !condition.module}
                  />
                </FormField>
              </>
            )}
            {condition.params.sourceFields.map((sourceField, sfIndex) => (
              <SpaceBetween key={sfIndex} direction="vertical" size="s">
                <Box variant="h4">Source {sfIndex + 1}</Box>
                <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                  <FormField
                    stretch
                    label={`Module ${sfIndex + 1}`}
                    errorText={errors[index]?.sourceFields?.[sfIndex]?.module}
                    constraintText="Required"
                  >
                    <Select
                      filteringType="auto"
                      placeholder="Select a Module"
                      selectedOption={sourceField.module || null}
                      onChange={({ detail }) => {
                        updateSourceField(index, sfIndex, "module", detail.selectedOption);
                        if (detail.selectedOption?.value) {
                          loadFields(detail.selectedOption, index, sfIndex);
                        }
                      }}
                      options={formattedModules}
                      disabled={loading}
                    />
                  </FormField>
                  <FormField
                    stretch
                    label={`Field ${sfIndex + 1}`}
                    errorText={errors[index]?.sourceFields?.[sfIndex]?.field}
                    constraintText="Required"
                  >
                    <Select
                      filteringType="auto"
                      placeholder="Select a field"
                      selectedOption={sourceField.field || null}
                      onChange={({ detail }) => updateSourceField(index, sfIndex, "field", detail.selectedOption)}
                      options={sourceField.availableFields || []}
                      disabled={loading || !sourceField.module}
                    />
                  </FormField>
                </Grid>
                {condition.type !== "SAME" && (
                  <FormField
                    stretch
                    label={`Operation ${sfIndex + 1}`}
                    errorText={errors[index]?.sourceFields?.[sfIndex]?.operation}
                    constraintText="Required"
                  >
                    <Select
                      filteringType="auto"
                      placeholder="Select an operation"
                      selectedOption={sourceField.operation || null}
                      onChange={({ detail }) => updateSourceField(index, sfIndex, "operation", detail.selectedOption)}
                      options={operations}
                      disabled={loading}
                    />
                  </FormField>
                )}
                {sourceField.module && (
                  <Container header={<Box variant="h4">Add Sub-Condition for Source {sfIndex + 1}</Box>}>
                    <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                      <Button
                        variant="primary"
                        iconName="add-plus"
                        onClick={() => addSubCondition(index, "DATE_CONDITIONAL", sfIndex)}
                        disabled={loading}
                      >
                        Add Date Conditional
                      </Button>
                      <Button
                        variant="primary"
                        iconName="add-plus"
                        onClick={() => addSubCondition(index, "COMBINE_CONDITIONAL", sfIndex)}
                        disabled={loading}
                      >
                        Add Combine Conditional
                      </Button>
                    </Grid>
                  </Container>
                )}
                {sourceField.subConditions?.length > 0 && (
                  <ExpandableSection header={`Sub-Conditions for Source ${sfIndex + 1}`}>
                    <SpaceBetween direction="vertical" size="s">
                      {sourceField.subConditions.map((sub, subIndex) => (
                        <SubConditionForm
                          key={subIndex}
                          conditionIndex={index}
                          sourceFieldIndex={sfIndex}
                          subCondition={sub}
                          subIndex={subIndex}
                          condition={condition}
                          updateSubCondition={updateSubCondition}
                          updateSourceField={updateSourceField}
                          updateParam={updateParam}
                          removeSubCondition={removeSubCondition}
                          errors={errors[index]?.sourceFields?.[sfIndex]}
                          loading={loading}
                          availableFields={condition.availableFields}
                        />
                      ))}
                    </SpaceBetween>
                  </ExpandableSection>
                )}
              </SpaceBetween>
            ))}
            {condition.type !== "SAME" && (
              <>
                <FormField
                  stretch
                  label="Operator"
                  errorText={errors[index]?.operator}
                  constraintText="Required"
                >
                  <Select
                    filteringType="auto"
                    placeholder="Select an operator"
                    selectedOption={condition.operator || null}
                    onChange={({ detail }) => updateCondition(index, "operator", detail.selectedOption)}
                    options={operators}
                    disabled={loading}
                  />
                </FormField>
                <FormField
                  stretch
                  label="Value"
                  errorText={errors[index]?.value}
                  constraintText="Required (number)"
                >
                  <Input
                    type="number"
                    value={condition.params.value?.toString() || ""}
                    onChange={({ detail }) => updateParam(index, "value", detail.value)}
                    placeholder="Enter value (e.g., 0.5)"
                    disabled={loading}
                    stretch
                  />
                </FormField>
              </>
            )}
            {condition.type === "SAME" && (
              <>
                <FormField
                  stretch
                  label="Same Operator"
                  errorText={errors[index]?.sameOperator}
                  constraintText="Required"
                >
                  <Select
                    filteringType="auto"
                    placeholder="Select same operator"
                    selectedOption={condition.params.sameOperator || null}
                    onChange={({ detail }) => updateParam(index, "sameOperator", detail.selectedOption)}
                    options={operators}
                    disabled={loading}
                  />
                </FormField>
                <FormField
                  stretch
                  label="Match Count"
                  errorText={errors[index]?.matchCount}
                  constraintText="Optional (number of matching records)"
                >
                  <Input
                    type="number"
                    value={condition.params.matchCount || ""}
                    onChange={({ detail }) => updateParam(index, "matchCount", detail.value)}
                    placeholder="Enter match count (e.g., 2)"
                    disabled={loading}
                    stretch
                  />
                </FormField>
              </>
            )}
            {condition.type === "RATIO" && (
              <FormField
                stretch
                label="Result Format"
                errorText={errors[index]?.resultFormat}
                constraintText="Required"
              >
                <Select
                  filteringType="auto"
                  placeholder="Select result format"
                  selectedOption={condition.params.resultFormat || null}
                  onChange={({ detail }) => updateParam(index, "resultFormat", detail.selectedOption)}
                  options={resultFormats}
                  disabled={loading}
                />
              </FormField>
            )}
          </SpaceBetween>
        ) : ["COUNT", "SUM", "COUNT_DISTINCT"].includes(condition.type) ? (
          <SpaceBetween direction="vertical" size="s">
            <FormField
              stretch
              label="Module"
              errorText={errors[index]?.module}
              constraintText="Required"
            >
              <Select
                filteringType="auto"
                placeholder="Select a module"
                selectedOption={condition.module || null}
                onChange={({ detail }) => {
                  updateCondition(index, "module", detail.selectedOption);
                  if (detail.selectedOption?.value) {
                    loadFields(detail.selectedOption, index);
                  }
                }}
                options={formattedModules}
                disabled={loading}
              />
            </FormField>
            <FormField
              stretch
              label="Operator"
              errorText={errors[index]?.operator}
              constraintText="Required"
            >
              <Select
                filteringType="auto"
                placeholder="Select an operator"
                selectedOption={condition.operator || null}
                onChange={({ detail }) => updateCondition(index, "operator", detail.selectedOption)}
                options={operators}
                disabled={loading}
              />
            </FormField>
            <FormField
              stretch
              label={(condition.type === "COUNT" || condition.type === "COUNT_DISTINCT") ? "Count Field" : "Sum Field"}
              errorText={errors[index]?.sourceFields?.[0]?.field}
              constraintText="Required"
            >
              <Select
                filteringType="auto"
                placeholder={`Select a ${condition.type.toLowerCase()} field`}
                selectedOption={condition.params.sourceFields[0]?.field || null}
                onChange={({ detail }) => updateSourceField(index, 0, "field", detail.selectedOption)}
                options={condition.availableFields.filter((e) => !e.type?.includes("date")) || []}
                disabled={loading || !condition.module}
              />
            </FormField>
            <FormField
              stretch
              label="Threshold Value"
              errorText={errors[index]?.value}
              constraintText="Required (positive number)"
            >
              <Input
                type="number"
                value={condition.params.value?.toString() || ""}
                onChange={({ detail }) => updateParam(index, "value", detail.value)}
                placeholder="Enter threshold (e.g., 100)"
                disabled={loading}
                stretch
              />
            </FormField>
            <Container header={<Box variant="h4">Add Sub-Condition</Box>}>
              <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                <Button
                  variant="primary"
                  iconName="add-plus"
                  onClick={() => addSubCondition(index, "DATE_CONDITIONAL")}
                  disabled={loading || !condition.module}
                >
                  Add Date Conditional
                </Button>
                <Button
                  variant="primary"
                  iconName="add-plus"
                  onClick={() => addSubCondition(index, "COMBINE_CONDITIONAL")}
                  disabled={loading || !condition.module}
                >
                  Add Combine Conditional
                </Button>
              </Grid>
            </Container>
            {condition.params.sourceFields[0]?.subConditions?.length > 0 && (
              <ExpandableSection header="Sub-Conditions">
                <SpaceBetween direction="vertical" size="s">
                  {condition.params.sourceFields[0].subConditions.map((sub, subIndex) => (
                    <SubConditionForm
                      key={subIndex}
                      conditionIndex={index}
                      sourceFieldIndex={0}
                      subCondition={sub}
                      subIndex={subIndex}
                      condition={condition}
                      updateSubCondition={updateSubCondition}
                      updateSourceField={updateSourceField}
                      updateParam={updateParam}
                      removeSubCondition={removeSubCondition}
                      errors={errors[index]}
                      loading={loading}
                      availableFields={condition.availableFields}
                    />
                  ))}
                </SpaceBetween>
              </ExpandableSection>
            )}
          </SpaceBetween>
        ) : (
          <>
            <FormField
              label="Module"
              errorText={errors[index]?.module}
              constraintText="Required"
              stretch
            >
              <Select
                filteringType="auto"
                placeholder="Select a module"
                selectedOption={condition.module || null}
                onChange={({ detail }) => {
                  updateCondition(index, "module", detail.selectedOption);
                  if (detail.selectedOption?.value) {
                    loadFields(detail.selectedOption, index);
                  }
                }}
                options={formattedModules}
                disabled={loading}
              />
            </FormField>
            <FormField
              label="Field"
              errorText={errors[index]?.field}
              constraintText="Required"
              stretch
            >
              <Select
                filteringType="auto"
                placeholder="Select a field"
                selectedOption={condition.field || null}
                onChange={({ detail }) => updateCondition(index, "field", detail.selectedOption)}
                options={condition.availableFields || []}
                disabled={loading || !condition.module}
              />
            </FormField>
            {!(condition.type === "DATE_SUB" && getDateMode(condition) === "range") && (
            <FormField
              label="Operator"
              errorText={errors[index]?.operator}
              constraintText="Required"
              stretch
            >
              <Select
                filteringType="auto"
                placeholder="Select an operator"
                selectedOption={condition.operator || null}
                onChange={({ detail }) => updateCondition(index, "operator", detail.selectedOption)}
                options={condition.type === "DATE_SUB" ? dateOperators : operators}
                disabled={loading || !condition.field}
              />
            </FormField>
            )}
            {condition.type === "DATE_SUB" && (
              <FormField stretch label="Date mode" errorText={errors[index]?.dateMode}>
                <Select
                  filteringType="auto"
                  placeholder="Select a date mode"
                  selectedOption={dateModes.find((option) => option.value === getDateMode(condition)) || dateModes[0]}
                  onChange={({ detail }) => updateParam(index, "dateMode", detail.selectedOption)}
                  options={dateModes}
                  disabled={loading}
                />
              </FormField>
            )}
            {(condition.type !== "DATE_SUB" || getDateMode(condition) === "relative") && (
            <FormField
              label="Value"
              errorText={errors[index]?.value}
              constraintText="Required"
              stretch
            >
              {["IN","NOT IN"].includes(condition.operator?.value) ? (
                <SpaceBetween direction="horizontal" size="xs">
                  <Input
                    value={condition.inInputValue || ""}
                    onChange={({ detail }) => updateCondition(index, "inInputValue", detail.value)}
                    placeholder="Enter value"
                    disabled={loading}
                    stretch
                  />
                  <Button
                    iconName="add-plus"
                    onClick={() => {
                      if (condition.inInputValue) {
                        const newValue = [
                          ...(condition.value || []),
                          { label: condition.inInputValue, value: condition.inInputValue },
                        ];
                        updateCondition(index, "value", newValue);
                        updateCondition(index, "inInputValue", "");
                      }
                    }}
                    disabled={loading || !condition.inInputValue}
                    variant="primary"
                    stretch
                  >
                    Add IN
                  </Button>
                  <Box>
                    {condition.value?.length > 0 ? (
                      condition.value.map((val, valIndex) => (
                        <Box
                          key={valIndex}
                          padding="xxs"
                          backgroundColor="grey-200"
                          borderRadius="small"
                          margin={{ right: "xs" }}
                          display="inline-block"
                        >
                          {val.value}
                          <Button
                            iconName="close"
                            variant="link"
                            onClick={() =>
                              updateCondition(index, "value", condition.value.filter((v) => v.value !== val.value))
                            }
                            disabled={loading}
                            stretch
                          />
                        </Box>
                      ))
                    ) : (
                      <Box color="text-status-info">No values added</Box>
                    )}
                  </Box>
                </SpaceBetween>
              ) : (
                <Input
                  value={condition.params.value?.toString() || ""}
                  onChange={({ detail }) => updateParam(index, "value", detail.value)}
                  placeholder="Enter value"
                  disabled={loading || !condition.operator}
                  stretch
                />
              )}
            </FormField>
            )}
            {condition.type === "DATE_SUB" && getDateMode(condition) === "relative" && (
              <FormField
                label="Unit"
                errorText={errors[index]?.unit}
                constraintText="Required"
                stretch
              >
                <Select
                  placeholder="Select a unit"
                  selectedOption={condition.params.unit || null}
                  onChange={({ detail }) => updateParam(index, "unit", detail.selectedOption)}
                  options={units}
                  disabled={loading}
                />
              </FormField>
            )}
            {condition.type === "DATE_SUB" && getDateMode(condition) === "fixed" && (
              <Grid gridDefinition={fixedDateGridDefinition}>
                <FormField stretch label="Date" errorText={errors[index]?.fixedDate}>
                  <DatePicker
                    value={condition.params.fixedDate || ""}
                    onChange={({ detail }) => updateParam(index, "fixedDate", detail.value)}
                    placeholder="YYYY-MM-DD"
                    disabled={loading}
                  />
                </FormField>
                <FormField stretch label="Time" constraintText="Optional. Defaults to 00:00" errorText={errors[index]?.fixedTime}>
                  <div style={{ maxWidth: "234px" }}>
                    <TimeInput
                      value={condition.params.fixedTime || ""}
                      onChange={({ detail }) => updateParam(index, "fixedTime", detail.value)}
                      format="hh:mm"
                      placeholder="hh:mm"
                      disabled={loading}
                    />
                  </div>
                </FormField>
              </Grid>
            )}
            {condition.type === "DATE_SUB" && getDateMode(condition) === "range" && (
              <FormField stretch label="Date range" errorText={errors[index]?.rangeStart || errors[index]?.rangeEnd || errors[index]?.rangeOrder}>
                <DateRangePicker
                  value={
                    buildDateTimeValue(condition.params.rangeStartDate, condition.params.rangeStartTime) && buildDateTimeValue(condition.params.rangeEndDate, condition.params.rangeEndTime)
                      ? {
                        type: "absolute",
                        startDate: buildDateTimeValue(condition.params.rangeStartDate, condition.params.rangeStartTime),
                        endDate: buildDateTimeValue(condition.params.rangeEndDate, condition.params.rangeEndTime),
                      }
                      : null
                  }
                  onChange={({ detail }) => {
                    const start = splitDateTimeValue(detail.value?.startDate);
                    const end = splitDateTimeValue(detail.value?.endDate);
                    updateParam(index, "rangeStartDate", start.date);
                    updateParam(index, "rangeStartTime", start.time);
                    updateParam(index, "rangeEndDate", end.date);
                    updateParam(index, "rangeEndTime", end.time);
                  }}
                  dateOnly={false}
                  timeInputFormat="hh:mm"
                  rangeSelectorMode="absolute-only"
                  relativeOptions={[]}
                  showClearButton
                  isValidRange={(range) => {
                    if (!range?.startDate) return { valid: false, errorMessage: "Select a start date and time." };
                    if (!range?.endDate) return { valid: false, errorMessage: "Select an end date and time." };
                    if (new Date(range.startDate).getTime() > new Date(range.endDate).getTime()) return { valid: false, errorMessage: "The start must not be later than the end." };
                    return { valid: true };
                  }}
                  i18nStrings={dateRangeI18nStrings}
                  placeholder="Select start and end"
                  disabled={loading}
                />
              </FormField>
            )}
            {condition.type === "DATE_SUB" && (
              <FormField stretch>
                <Toggle
                  checked={condition.params.isUnixTimestamp || false}
                  onChange={({ detail }) => updateParam(index, "isUnixTimestamp", detail.checked)}
                  disabled={loading}
                >
                  Unix Time
                </Toggle>
              </FormField>
            )}
          </>
        )}
        <Button
          iconName="remove"
          onClick={() => removeCondition(index)}
          disabled={loading}
          variant="link"
        >
          Remove Condition
        </Button>
      </SpaceBetween>
    </Container>
  );
};

const PlayerRulesEditModal = ({ playerRulesEditModal, setPlayerRulesEditModal, Interface, user }) => {
  const [ruleName, setRuleName] = useState("");
  const [secondaryName, setSecondaryName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useColor(playerRulesEditModal.rule?.color || "rgb(86 30 203)");
  const [secondaryColor, setSecondaryColor] = useColor(playerRulesEditModal.rule?.secondaryColor || "rgb(86 30 203)");
  const [enabledRule, setEnabledRule] = useState(null);
  const [conditionType, setConditionType] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [requestAction, setRequestAction] = useState(null);
  const [ruleId, setRuleId] = useState(null);
  const [external, setExternal] = useState(false);
  const [fixedTag, setFixedTag] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSecondaryColorPicker, setShowSecondaryColorPicker] = useState(false);


  const [actionValue, setActionValue] = useState("");
  const [additionalValue, setAdditionalValue] = useState("");
  const [actionType, setActionType] = useState(false);
  const [project, setProject] = useState([]);

  const [automaticAction, setAutomaticAction] = useState({ value: "none", label: "None" });

  const brandList = useMemo(() => user.brandsList.map((e) => ({label: e.name, value: e.id})), [user.brandsList]);

  const formatModuleFields = useCallback((fields) => (
    Array.isArray(fields)
      ? fields.map((el) => ({
        label: el.label || el.value || el.field,
        value: el.value || el.field,
        type: el.type,
      }))
      : []
  ), []);

  const loadModuleFields = useCallback(async (moduleName) => {
    if (!moduleName) {
      return [];
    }

    const fields = await Interface.request(
      "get",
      {
        class: "panel",
        module: "playerRules",
        method: "getModule",
      },
      { module: moduleName }
    );

    return formatModuleFields(fields);
  }, [Interface, formatModuleFields]);

  const resetAutomaticFields = useCallback(() => {
    setActionType(false);
    setActionValue("");
    setAdditionalValue("")
  }, []);

  const getOptionValue = (option) => {
    if (!option) {
      return null;
    }

    return typeof option === "string" ? option || null : option.value || null;
  };

  const getConditionKind = (operation, type) => {
    if (operation) {
      return operation;
    }

    if (conditionTypes.some((item) => item.value === type)) {
      return type;
    }

    return "SIMPLE";
  };

  const createOption = (value) => {
    if (!value) {
      return null;
    }

    return { value, label: value };
  };

  const findModuleOption = useCallback((moduleName) => {
    if (!moduleName) {
      return null;
    }

    const matchedModule = playerRulesEditModal.currentOption.module.find((moduleOption) => moduleOption.value === moduleName);

    if (matchedModule) {
      return {
        value: matchedModule.value,
        label: matchedModule.label,
      };
    }

    return {
      value: moduleName,
      label: moduleName,
    };
  }, [playerRulesEditModal.currentOption.module]);

  const findFieldOption = (fields, fieldName, fallbackType = null) => {
    if (!fieldName) {
      return null;
    }

    const matchedField = fields.find((field) => field.value === fieldName);

    if (matchedField) {
      return matchedField;
    }

    return {
      value: fieldName,
      label: fieldName,
      type: fallbackType || null,
    };
  };

  const buildSourceFieldDefaults = (conditionType, availableFields = []) => {
    if (["MINUS", "RATIO"].includes(conditionType)) {
      return [
        {
          module: null,
          field: null,
          operation: operations[0],
          availableFields,
          subConditions: [],
        },
        {
          module: null,
          field: null,
          operation: operations[0],
          availableFields,
          subConditions: [],
        },
      ];
    }

    if (["SAME", "COUNT", "SUM"].includes(conditionType)) {
      return [
        {
          module: null,
          field: null,
          availableFields,
          subConditions: [],
        },
      ];
    }

    return [];
  };

  function parseStoredValue(op, val) {
    const operatorValue = typeof op === "string" ? op : op?.value;
    if (["IN", "NOT IN"].includes(operatorValue)) {
      if (Array.isArray(val)) return val.map(v => ({ label: v, value: v }));
      if (typeof val === "string")
        return val.split(",").map(v => v.trim()).filter(Boolean).map(v => ({ label: v, value: v }));
      if (val == null) return [];
      return [{ label: String(val), value: String(val) }];
    }
    return val == null ? "" : String(val);
  }
  function parseStoredListValue(op, val) {
    const operatorValue = typeof op === "string" ? op : op?.value;
    if (["IN", "NOT IN"].includes(operatorValue)) {
      if (Array.isArray(val)) {
        return val.map(v => ({ label: v, value: v }));
      }
      if (typeof val === "string") {
        return val.split(",").map(v => v.trim()).filter(Boolean).map(v => ({ label: v, value: v }));
      }
      if (val == null) {
        return [];
      }
      return [{ label: String(val), value: String(val) }];
    }
    return [];
  }

  const loadRuleData = useCallback(async (rule) => {
    setLoading(true);
    try {
      const name = rule.name;
      const secondaryName = rule.secondaryName || "";
      const description = rule.description || "";
      const brand = rule.brand ? JSON.parse(rule.brand) : [];
      const automaticAction = rule.automaticAction ? JSON.parse(rule.automaticAction) : null;
      const external = Boolean(rule.external);
      const fixedTag = rule.fixedTag === true || Number(rule.fixedTag) === 1 || String(rule.fixedTag).toLowerCase() === "true";
      const enabled = rule.enabled
        ? { label: "ENABLED", value: "ENABLED" }
        : { label: "DISABLED", value: "DISABLED" };
      const id = rule.id;
      const expression = JSON.parse(rule.expression);
      const { conditions } = expression;


      const formattedConditions = await Promise.all(
        conditions.map(async (condition) => {
          const conditionType = getConditionKind(condition.operation, condition.type);
          const conditionFields = await loadModuleFields(condition.table);
          const conditionOperator = createOption(condition.operator);
          const conditionValue = getOptionValue(conditionOperator);

          const newCondition = {
            type: conditionType,
            module: findModuleOption(condition.table),
            field: findFieldOption(conditionFields, condition.field, condition.type),
            operator: conditionOperator,
            value: parseStoredListValue(condition.operator, condition.value),
            inInputValue: "",
            operation: conditionType !== "SIMPLE"
              ? {
                value: condition.operation || conditionType,
                label: condition.operation || conditionType,
              }
              : null,
            params: {
              sourceFields: condition.params?.sourceFields?.length
                ? await Promise.all(
                  condition.params.sourceFields.map(async (sf) => {
                    const sourceFields = sf.table
                      ? await loadModuleFields(sf.table)
                      : conditionFields;

                    const sourceField = {
                      module: findModuleOption(sf.table),
                      field: findFieldOption(sourceFields, sf.field, sf.type),
                      operation: conditionType === "SAME"
                        ? null
                        : createOption(sf.operation || (["MINUS", "RATIO"].includes(conditionType) ? operations[0]?.value : null)),
                      availableFields: sourceFields,
                      subConditions: (sf.subConditions || []).map((sub) => {
                        const subValue = parseStoredValue(sub.operator, sub.value);
                        const fixedDate = splitDateTimeValue(sub.fixedDate);
                        const rangeStart = splitDateTimeValue(sub.rangeStart);
                        const rangeEnd = splitDateTimeValue(sub.rangeEnd);

                        return {
                          type: sub.type,
                          operator: createOption(sub.operator),
                          inInputValue: "",
                          params: {
                            sourceFields: [
                              {
                                field: findFieldOption(sourceFields, sub.field),
                                value: subValue,
                                availableFields: sourceFields,
                              },
                            ],
                            unit: createOption(sub.unit),
                            dateMode: dateModes.find((option) => option.value === (sub.mode || "relative")) || dateModes[0],
                            fixedDate: fixedDate.date,
                            fixedTime: fixedDate.time,
                            rangeStartDate: rangeStart.date,
                            rangeStartTime: rangeStart.time,
                            rangeEndDate: rangeEnd.date,
                            rangeEndTime: rangeEnd.time,
                            logicalOperator: createOption(sub.logicalOperator),
                            isUnixTimestamp: sub.isUnixTimestamp || false,
                            value: subValue,
                          },
                          availableFields: sourceFields,
                        };
                      }),
                    };

                    return sourceField;
                  })
                )
                : buildSourceFieldDefaults(conditionType, conditionFields),
              matchCount: condition.params?.matchCount !== undefined && condition.params?.matchCount !== null
                ? condition.params.matchCount.toString()
                : "",
              sameOperator: createOption(condition.params?.sameOperator),
              value: ["IN", "NOT IN"].includes(conditionValue)
                ? ""
                : condition.params?.value?.toString() || condition.value?.toString() || "",
              unit: createOption(condition.params?.unit),
              dateMode: dateModes.find((option) => option.value === (condition.params?.mode || "relative")) || dateModes[0],
              fixedDate: splitDateTimeValue(condition.params?.fixedDate).date,
              fixedTime: splitDateTimeValue(condition.params?.fixedDate).time,
              rangeStartDate: splitDateTimeValue(condition.params?.rangeStart).date,
              rangeStartTime: splitDateTimeValue(condition.params?.rangeStart).time,
              rangeEndDate: splitDateTimeValue(condition.params?.rangeEnd).date,
              rangeEndTime: splitDateTimeValue(condition.params?.rangeEnd).time,
              isUnixTimestamp: condition.params?.isUnixTimestamp || false,
              resultFormat: createOption(condition.params?.resultFormat),
            },
            availableFields: conditionFields,
          };

          return newCondition;
        })
      );

      setRuleName(name);
      setErrors({});
      setSecondaryName(secondaryName);
      setDescription(description);
      setExternal(external);
      setFixedTag(fixedTag);
      setConditions(formattedConditions);
      setEnabledRule(enabled);
      setRuleId(id);
      setProject(brand.map((e) => brandList.find((element) => element.value === e)).filter(Boolean));

      if (automaticAction) {
        setAutomaticAction(playerRulesEditModal.currentOption.automaticPool.find((e) => e.value === automaticAction.type) || { value: "none", label: "None" });
        setActionType(playerRulesEditModal.currentOption.actionBan.find((e) => e.value === automaticAction.actionType) || false);
        if (typeof automaticAction.actionValue !== "undefined") {
          setActionValue(automaticAction.actionValue);
        }
        if (typeof automaticAction.additionalValue !== "undefined") {
          setAdditionalValue(automaticAction.additionalValue);
        }
      } else {
        setAutomaticAction({ value: "none", label: "None" });
        resetAutomaticFields();
      }
    } catch (error) {
      console.log(error);
      Interface.addNoty("Error", `Failed to load rule data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [Interface, brandList, findModuleOption, loadModuleFields, playerRulesEditModal.currentOption.actionBan, playerRulesEditModal.currentOption.automaticPool, resetAutomaticFields]);

  useEffect(() => {
    if (playerRulesEditModal.rule) {
      loadRuleData(playerRulesEditModal.rule);
    }
  }, [loadRuleData, playerRulesEditModal.rule]);

  const loadFields = useCallback(
    async (module, conditionIndex, sourceFieldIndex = 0, subConditionIndex = null) => {
      if (!module?.value) {
        Interface.addNoty("Error", "Invalid module selected");
        return;
      }
      setLoading(true);
      try {
        const formattedFields = await loadModuleFields(module.value);

        setConditions((prevConditions) => {
          const newConditions = cloneDeep(prevConditions);
          const currentCondition = newConditions[conditionIndex];

          if (!currentCondition.params.sourceFields[sourceFieldIndex]) {
            currentCondition.params.sourceFields[sourceFieldIndex] = {
              field: null,
              availableFields: formattedFields,
              subConditions: [],
            };
          } else {
            currentCondition.params.sourceFields[sourceFieldIndex].availableFields = formattedFields;
          }
          currentCondition.availableFields = formattedFields;

          if (subConditionIndex !== null) {
            const subCondition = currentCondition.params.sourceFields[sourceFieldIndex].subConditions[subConditionIndex];
            if (subCondition) {
              subCondition.availableFields = formattedFields;
              if (!subCondition.params.sourceFields[0]) {
                subCondition.params.sourceFields[0] = {
                  field: null,
                  availableFields: formattedFields,
                  value: "",
                };
              } else {
                subCondition.params.sourceFields[0].availableFields = formattedFields;
              }
            }
          }
          return newConditions;
        });
      } catch (error) {
        Interface.addNoty("Error", `Failed to load fields for module ${module.value}: ${error.message}`);
      } finally {
        setLoading(false);
      }
    },
    [Interface, loadModuleFields]
  );

  const updateConditionType = useCallback((type) => {
    setConditionType(type);
  }, []);

  const updateRuleName = useCallback((value) => {
    setRuleName(value);
  }, []);

  const addCondition = useCallback(() => {
    if (!conditionType) {
      Interface.addNoty("Error", "Please select a condition type");
      return;
    }
    if (["DATE_CONDITIONAL", "COMBINE_CONDITIONAL"].includes(conditionType.value)) {
      if (conditions.length === 0) {
        Interface.addNoty("Error", "Please add a parent condition first");
        return;
      }
      setConditions((prevConditions) => {
        const newConditions = cloneDeep(prevConditions);
        const parentCondition = newConditions[conditions.length - 1];
        if (!parentCondition.params.sourceFields[0]) {
          parentCondition.params.sourceFields[0] = { field: null, availableFields: [], subConditions: [] };
        }
        parentCondition.params.sourceFields[0].subConditions.push({
          type: conditionType.value,
          module: parentCondition.module,
          operator: null,
          inInputValue: "",
          params: {
            unit: null,
            dateMode: dateModes[0],
            fixedDate: "",
            fixedTime: "",
            rangeStartDate: "",
            rangeStartTime: "",
            rangeEndDate: "",
            rangeEndTime: "",
            sourceFields: [{ field: null, availableFields: parentCondition.availableFields || [], value: "" }],
            value: conditionType.value === "COMBINE_CONDITIONAL" ? [] : "",
            isUnixTimestamp: false,
            logicalOperator: { value: "AND", label: "AND" },
          },
          availableFields: parentCondition.availableFields || [],
        });
        if (parentCondition.module?.value) {
          loadFields(
            parentCondition.module,
            conditions.length - 1,
            0,
            parentCondition.params.sourceFields[0].subConditions.length - 1
          );
        }
        return newConditions;
      });
    } else {
      const newCondition = {
        type: conditionType.value,
        module: null,
        field: null,
        operator: null,
        value: [],
        inInputValue: "",
        operation: ["MINUS", "DATE_SUB", "SAME", "COUNT", "COUNT_DISTINCT", "SUM", "RATIO"].includes(conditionType.value)
          ? { value: conditionType.value, label: conditionType.label }
          : null,
        params: {
          unit: null,
          ...createDateModeParams(),
          sourceFields: ["MINUS", "RATIO"].includes(conditionType.value)
            ? [
              { module: null, field: null, operation: operations[0], availableFields: [], subConditions: [] },
              { module: null, field: null, operation: operations[0], availableFields: [], subConditions: [] },
            ]
            : ["SAME", "COUNT", "COUNT_DISTINCT", "SUM"].includes(conditionType.value)
              ? [{ module: null, field: null, availableFields: [], subConditions: [] }]
              : [],
          value: "",
          isUnixTimestamp: false,
          resultFormat: conditionType.value === "RATIO" ? { value: "NUMBER", label: "Number (e.g., 0.8)" } : null,
          sameOperator: conditionType.value === "SAME" ? null : undefined,
          matchCount: conditionType.value === "SAME" ? "" : undefined,
        },
        availableFields: [],
      };
      setConditions((prevConditions) => [...prevConditions, newCondition]);
    }
  }, [conditionType, conditions, loadFields, Interface]);

  const updateCondition = useCallback(
    (index, key, value) => {
      setConditions((prevConditions) => {
        const newConditions = cloneDeep(prevConditions);
        newConditions[index][key] = value;
        if (key === "module" && value?.value) {
          loadFields(value, index);
        }
        return newConditions;
      });
    },
    [loadFields]
  );

  const updateParam = useCallback((index, paramKey, value, subConditionIndex = null, sourceFieldIndex = null) => {
    setConditions((prevConditions) => {
      const newConditions = cloneDeep(prevConditions);
      if (subConditionIndex !== null && sourceFieldIndex !== null) {
        const subCondition = newConditions[index].params.sourceFields[sourceFieldIndex]?.subConditions[subConditionIndex];
        if (subCondition) {
          subCondition.params[paramKey] = value;
        }
      } else {
        newConditions[index].params[paramKey] = value;
      }
      return newConditions;
    });
  }, []);

  const updateSubCondition = useCallback((conditionIndex, subConditionIndex, key, value, sourceFieldIndex = 0) => {
    setConditions((prevConditions) => {
      const newConditions = cloneDeep(prevConditions);
      const subConditions = newConditions[conditionIndex].params.sourceFields[sourceFieldIndex]?.subConditions || [];
      if (subConditions[subConditionIndex]) {
        if (key === "value") {
          subConditions[subConditionIndex].params[key] = value;
        } else {
          subConditions[subConditionIndex][key] = value;
        }
      }
      return newConditions;
    });
  }, []);

  const addSubCondition = useCallback(
    (conditionIndex, subConditionType, sourceFieldIndex = 0) => {
      setConditions((prevConditions) => {
        const newConditions = cloneDeep(prevConditions);
        const condition = newConditions[conditionIndex];

        if (!condition.params.sourceFields[sourceFieldIndex]) {
          condition.params.sourceFields[sourceFieldIndex] = { field: null, availableFields: [], subConditions: [] };
        }

        const newSubCondition = {
          type: subConditionType,
          module: condition.module,
          operator: null,
          inInputValue: "",
          params: {
            unit: null,
            dateMode: dateModes[0],
            fixedDate: "",
            fixedTime: "",
            rangeStartDate: "",
            rangeStartTime: "",
            rangeEndDate: "",
            rangeEndTime: "",
            sourceFields: [{ field: null, availableFields: condition.availableFields || [], value: "" }],
            value: subConditionType === "COMBINE_CONDITIONAL" ? [] : "",
            isUnixTimestamp: false,
            logicalOperator: { value: "AND", label: "AND" },
          },
          availableFields: condition.availableFields || [],
        };

        condition.params.sourceFields[sourceFieldIndex].subConditions.push(newSubCondition);
        if (condition.module?.value) {
          loadFields(
            condition.module,
            conditionIndex,
            sourceFieldIndex,
            condition.params.sourceFields[sourceFieldIndex].subConditions.length - 1
          );
        }
        return newConditions;
      });
    },
    [loadFields]
  );

  const removeSubCondition = useCallback((conditionIndex, subConditionIndex, sourceFieldIndex = 0) => {
    setConditions((prevConditions) => {
      const newConditions = cloneDeep(prevConditions);
      const subConditions = newConditions[conditionIndex].params.sourceFields[sourceFieldIndex]?.subConditions || [];
      if (subConditions.length > 0) {
        subConditions.splice(subConditionIndex, 1);
      }
      return newConditions;
    });
  }, []);

  const updateSourceField = useCallback(
    (index, sourceFieldIndex, key, value, subConditionIndex = null) => {
      setConditions((prevConditions) => {
        const newConditions = cloneDeep(prevConditions);
        const sourceFields = newConditions[index].params.sourceFields;

        if (subConditionIndex !== null && sourceFields[sourceFieldIndex]?.subConditions[subConditionIndex]) {
          const subCondition = sourceFields[sourceFieldIndex].subConditions[subConditionIndex];
          if (!subCondition.params.sourceFields[0]) {
            subCondition.params.sourceFields[0] = { field: null, availableFields: [], value: "" };
          }
          subCondition.params.sourceFields[0][key] = value;
        } else if (sourceFields[sourceFieldIndex]) {
          sourceFields[sourceFieldIndex][key] = value;
        } else {
          sourceFields[sourceFieldIndex] = { field: null, availableFields: [], subConditions: [], [key]: value };
        }
        return newConditions;
      });
    },
    []
  );

  const removeCondition = useCallback((index) => {
    setConditions((prevConditions) => prevConditions.filter((_, i) => i !== index));
  }, []);

  const resetForm = useCallback(() => {
    setRuleName("");
    setSecondaryName("");
    setDescription("");
    setConditionType(null);
    setConditions([]);
    setErrors({});
    setLoading(false);
    setRequestAction(null);
    setRuleId(null);
    setFixedTag(false);
  }, []);

  const validateConditions = useCallback(() => {
    const errors = {};
    let isValid = true;

    if (!ruleName.trim()) {
      errors.ruleName = "Rule name is required";
      isValid = false;
    }

    conditions.forEach((condition, index) => {
      errors[index] = {};
      if (!["MINUS", "SAME", "COUNT", "COUNT_DISTINCT", "SUM", "RATIO"].includes(condition.type)) {
        if (!condition.module) {
          errors[index].module = "Select a module";
          isValid = false;
        }
        if (!condition.field) {
          errors[index].field = "Select a field";
          isValid = false;
        }
        if (condition.type === "DATE_SUB") {
          if (!validateDateModeFields({ mode: getDateMode(condition), operator: condition.operator, params: condition.params }, errors[index])) {
            isValid = false;
          }
        } else {
          if (!condition.operator) {
            errors[index].operator = "Select an operator";
            isValid = false;
          }
          if (["IN","NOT IN"].includes(condition.operator?.value) && (!condition.value || condition.value.length === 0)) {
            errors[index].value = "Add at least one value for IN";
            isValid = false;
          } else if (condition.operator?.value !== "IN" && condition.operator?.value !== "NOT IN" && !condition.params.value) {
            errors[index].value = "Enter a value";
            isValid = false;
          }
        }
      } else if (["MINUS", "RATIO", "SAME"].includes(condition.type)) {
        if (condition.params.sourceFields.length < (condition.type === "SAME" ? 1 : 2)) {
          errors[index].sourceFields = [{ general: condition.type === "SAME" ? "At least one source field is required" : "Both source fields are required" }];
          isValid = false;
        }
        condition.params.sourceFields.forEach((sf, sfIndex) => {
          errors[index].sourceFields = errors[index].sourceFields || [];
          errors[index].sourceFields[sfIndex] = errors[index].sourceFields[sfIndex] || {};
          if (!sf.module) {
            errors[index].sourceFields[sfIndex].module = "Select a module";
            isValid = false;
          }
          if (!sf.field) {
            errors[index].sourceFields[sfIndex].field = "Select a field";
            isValid = false;
          }
          if (condition.type !== "SAME" && !sf.operation) {
            errors[index].sourceFields[sfIndex].operation = "Select an operation";
            isValid = false;
          }
          sf.subConditions?.forEach((sub, subIndex) => {
            errors[index].sourceFields[sfIndex].subConditions = errors[index].sourceFields[sfIndex].subConditions || [];
            errors[index].sourceFields[sfIndex].subConditions[subIndex] = errors[index].sourceFields[sfIndex].subConditions[subIndex] || {};
            if (!validateSubCondition(sub, errors[index].sourceFields[sfIndex].subConditions[subIndex])) isValid = false;
          });
        });
        if (condition.type === "SAME") {
          if (!condition.field) {
            errors[index].field = "Select a field for current record";
            isValid = false;
          }
          if (!condition.params.sameOperator) {
            errors[index].sameOperator = "Select a same operator";
            isValid = false;
          }
        } else {
          if (!condition.operator) {
            errors[index].operator = "Select an operator";
            isValid = false;
          }
          if (!condition.params.value) {
            errors[index].value = "Enter a value";
            isValid = false;
          }
        }
        if (condition.type === "RATIO" && !condition.params.resultFormat) {
          errors[index].resultFormat = "Select a result format";
          isValid = false;
        }
      } else if (["COUNT", "SUM", "COUNT_DISTINCT"].includes(condition.type)) {
        if (!condition.module) {
          errors[index].module = "Select a module";
          isValid = false;
        }
        if (!condition.operator) {
          errors[index].operator = "Select an operator";
          isValid = false;
        }
        if (!condition.params.sourceFields[0]?.field) {
          errors[index].sourceFields = errors[index].sourceFields || [];
          errors[index].sourceFields[0] = errors[index].sourceFields[0] || {};
          errors[index].sourceFields[0].field = "Select a field";
          isValid = false;
        }
        if (!condition.params.value) {
          errors[index].value = "Enter a threshold value";
          isValid = false;
        }
        condition.params.sourceFields.forEach((sf, sfIndex) => {
          errors[index].sourceFields = errors[index].sourceFields || [];
          errors[index].sourceFields[sfIndex] = errors[index].sourceFields[sfIndex] || {};
          sf.subConditions?.forEach((sub, subIndex) => {
            errors[index].sourceFields[sfIndex].subConditions = errors[index].sourceFields[sfIndex].subConditions || [];
            errors[index].sourceFields[sfIndex].subConditions[subIndex] = errors[index].sourceFields[sfIndex].subConditions[subIndex] || {};
            if (!validateSubCondition(sub, errors[index].sourceFields[sfIndex].subConditions[subIndex])) isValid = false;
          });
        });
      }
    });

    setErrors(errors);
    return isValid;
  }, [ruleName, conditions]);

  const renderAutomaticComponent = (type) => {
    if (type === "rejected_withdrawal" || type === "canceled_withdrawal") {
      return <FormField
        label="Note"
        errorText={errors.automaticNote}
        constraintText="Required"
        stretch
      >
        <Input
          value={actionValue}
          onChange={({ detail }) => setActionValue(detail.value)}
          placeholder="Enter note"
          disabled={loading}
          stretch
        />
      </FormField>
    }


    if (type === "user_ban" || type === "forbidden_deposit") {
      return (<>
        <FormField label="Select action type"
                   errorText={errors.actionType}
                   stretch>
          <Select
            filteringType="auto"
            placeholder="Select automatic action"
            selectedOption={actionType}
            onChange={({ detail }) => setActionType(detail.selectedOption)}
            options={playerRulesEditModal.currentOption.actionBan}
            disabled={loading}
            stretch
          />
        </FormField>
        <Box margin={{ top: "xs" }}></Box>
        <FormField
          label={type === "user_ban" ? "Reason" : "Group" }
          errorText={errors.additionalValue}
          constraintText="Required"
          stretch
        >
          <Input
            value={additionalValue}
            onChange={({ detail }) => setAdditionalValue(detail.value)}
            placeholder={type === "user_ban" ? "Enter reason" : "Enter group"}
            disabled={loading}
            stretch
          />
        </FormField>
        <Box margin={{ top: "xs" }}></Box>
        {actionType?.value === "date" && <>
          <FormField errorText={errors.actionBanDate} label="Select to Date">
            <DatePicker
              value={actionValue}
              onChange={({ detail }) => {
                setActionValue(detail.value);
              }}
              placeholder="To Date"
            />
          </FormField>
        </>}
      </>)
    }

    return null;
  }
  const formatValue = (value, type) => {
    if (!value) return null;
    if (Array.isArray(value)) {
      return value.map((v) => {
        if (type === "int" || type === "datetime") {
          return Number(v.value);
        } else if (type?.includes("decimal")) {
          return parseFloat(v.value);
        }
        return v.value;
      });
    }
    if (type === "int" || type === "datetime") {
      return Number(value);
    } else if (type?.includes("decimal")) {
      return parseFloat(value);
    }
    return value;
  };

  const cleanObject = (obj) => {
    const cleaned = {};
    Object.keys(obj).forEach((key) => {
      if (obj[key] !== null && obj[key] !== undefined && obj[key] !== "" && (!Array.isArray(obj[key]) || obj[key].length > 0)) {
        if (Array.isArray(obj[key])) {
          cleaned[key] = obj[key].map((item) => (typeof item === "object" ? cleanObject(item) : item));
        } else if (typeof obj[key] === "object") {
          const nested = cleanObject(obj[key]);
          if (Object.keys(nested).length > 0) {
            cleaned[key] = nested;
          }
        } else {
          cleaned[key] = obj[key];
        }
      }
    });
    return cleaned;
  };

  const formatNumericValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (typeof value === "number") {
      return value;
    }

    const parsedValue = Number(value);

    return Number.isNaN(parsedValue) ? value : parsedValue;
  };

  const buildConditionValue = (condition) => {
    if (condition.type === "SAME") {
      return null;
    }

    if (condition.type === "DATE_SUB" && getDateMode(condition) !== "relative") {
      return null;
    }

    if (["COUNT", "SUM", "MINUS", "RATIO"].includes(condition.type)) {
      return formatNumericValue(condition.params.value);
    }

    if (["IN", "NOT IN"].includes(getOptionValue(condition.operator))) {
      return formatValue(condition.value, condition.field?.type || "");
    }

    return formatValue(condition.params.value, condition.field?.type || "");
  };

  const buildFormattedConditions = () => conditions.map((condition) => {
    const result = {
      table: condition.module?.value || null,
      field: condition.field?.value || null,
      operator: condition.type === "SAME" || (condition.type === "DATE_SUB" && getDateMode(condition) === "range") ? null : getOptionValue(condition.operator),
      value: buildConditionValue(condition),
      operation: condition.type === "SIMPLE" ? null : getOptionValue(condition.operation) || condition.type || null,
    };

    if (condition.type !== "SIMPLE") {
      const params = {
        sourceFields: (condition.params.sourceFields || []).map((sf) => ({
          field: sf.field?.value || null,
          type: sf.field?.type || null,
          table: sf.module?.value || null,
          operation: condition.type === "SAME" ? null : getOptionValue(sf.operation),
          subConditions: sf.subConditions?.map((sub) => {
            const formattedSubCondition = {
              type: sub.type || null,
              field: sub.params.sourceFields[0]?.field?.value || null,
              operator: getOptionValue(sub.operator),
              value: formatValue(sub.params.value, sub.params.sourceFields[0]?.field?.type || ""),
              logicalOperator: getOptionValue(sub.params.logicalOperator),
              isUnixTimestamp: sub.params.isUnixTimestamp || false,
            };

            if (sub.type === "DATE_CONDITIONAL") {
              applyDateModePayload(formattedSubCondition, sub);
            }

            return cleanObject(formattedSubCondition);
          }),
        })),
        value: buildConditionValue(condition),
        unit: condition.params.unit?.value || null,
        isUnixTimestamp: condition.params.isUnixTimestamp || false,
        resultFormat: getOptionValue(condition.params.resultFormat),
        sameOperator: getOptionValue(condition.params.sameOperator),
        matchCount: formatNumericValue(condition.params.matchCount),
      };

      if (condition.type === "DATE_SUB") {
        applyDateModePayload(params, condition);
      }

      result.params = cleanObject(params);
    }

    return cleanObject(result);
  });

  const buildExpression = () => ({
    conditions: buildFormattedConditions(),
  });

  const getRequestMessage = (response, fallbackMessage) => {
    if (typeof response === "string" && response) {
      return response;
    }

    if (response?.text) {
      return response.text;
    }

    if (response?.message) {
      return response.message;
    }

    return fallbackMessage;
  };

  const getValidationFeedback = (response) => {
    if (response?.valid === false) {
      return {
        name: "Error",
        text: Array.isArray(response.errors) && response.errors.length > 0
          ? response.errors.join(" | ")
          : getRequestMessage(response, "Rule check failed"),
      };
    }

    return {
      name: "Success",
      text: getRequestMessage(response, "Rule checked successfully"),
    };
  };

  const checkConditions = useCallback(async () => {
    if (!validateConditions()) {
      Interface.addNoty("Error", "Please fill all required fields");
      return;
    }

    const expression = buildExpression();

    setRequestAction("check");
    try {
      const response = await Interface.request(
        "get",
        {
          class: "panel",
          module: "playerRules",
          method: "validate",
        },
        { expression }
      );

      const validationFeedback = getValidationFeedback(response);
      Interface.addNoty(validationFeedback.name, validationFeedback.text);
    } catch (error) {
      Interface.addNoty("Error", error.text || error.message || "Failed to check rule");
    } finally {
      setRequestAction(null);
    }
  }, [validateConditions, Interface, conditions]);

  const saveConditions = useCallback(async () => {
    if (!validateConditions()) {
      Interface.addNoty("Error", "Please fill all required fields");
      return;
    }

    const expression = buildExpression();

    const brand = project.map((e) => e.value);

    const body = automaticAction.value === "none"
      ? {
        id: ruleId,
        name: ruleName,
        description,
        external,
        fixedTag,
        enabled: enabledRule.value,
        color: color.hex,
        secondaryColor: secondaryColor.hex,
        expression,
        brand,
      }
      : {
        id: ruleId,
        name: ruleName,
        description,
        automaticAction: { type: automaticAction.value, actionType: actionType?.value, actionValue, additionalValue },
        external,
        fixedTag,
        enabled: enabledRule.value,
        color: color.hex,
        secondaryColor: secondaryColor.hex,
        expression,
        brand,
      };
    const trimmedSecondaryName = secondaryName.trim();

    if (trimmedSecondaryName) {
      body.secondaryName = trimmedSecondaryName;
    }

    setRequestAction("save");
    try {
      await Interface.request(
        "set",
        {
          class: "panel",
          module: "playerRules",
          method: "update",
        },
        body
      );

      Interface.addNoty("Success", "Conditions updated successfully");
      setPlayerRulesEditModal({ ...playerRulesEditModal, show: false, rule: null });
      resetForm();
      Interface.loadPlayerRulesList();
    } catch (error) {
      Interface.addNoty("Error", `Failed to save conditions: ${error.message}`);
    } finally {
      setRequestAction(null);
    }
  }, [ruleName, secondaryName, description, conditions, ruleId, validateConditions, Interface, setPlayerRulesEditModal, resetForm, enabledRule, color, secondaryColor, external, fixedTag, automaticAction, actionType, actionValue, additionalValue, project]);


  const formattedModules = Array.isArray(playerRulesEditModal.currentOption?.module)
    ? playerRulesEditModal.currentOption.module.map((mod) => ({
      label: mod.label || mod.value || mod,
      value: mod.value || mod,
    }))
    : [];



  return (
    <Modal
      visible={playerRulesEditModal.show}
      onDismiss={() => {
        setPlayerRulesEditModal({ ...playerRulesEditModal, show: false, rule: null });
        resetForm();
      }}
      header="Edit Player Rule"
      size="max"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="m">
            <Button
              variant="normal"
              disabled={loading || Boolean(requestAction)}
              onClick={() => {
                setPlayerRulesEditModal({ ...playerRulesEditModal, show: false, rule: null });
                resetForm();
              }}
            >
              Close
            </Button>
            <Button
              variant="normal"
              disabled={loading || Boolean(requestAction)}
              loading={requestAction === "check"}
              onClick={checkConditions}
            >
              Check rule
            </Button>
            <Button
              variant="primary"
              disabled={loading || Boolean(requestAction)}
              onClick={saveConditions}
              loading={requestAction === "save"}
              iconName="check"
            >
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Box margin={{bottom: "xl"}}>
        <SpaceBetween direction="vertical" size="xxl">
          <Container header={<Box display="flex" variant="h3">Automatic settings</Box>}>
            <FormField label="Automatic action"
                       errorText={errors.automaticAction}
                       stretch>
              <Select
                filteringType="auto"
                placeholder="Select automatic action"
                selectedOption={automaticAction}
                onChange={({ detail }) => {
                  setAutomaticAction(detail.selectedOption);
                  resetAutomaticFields();
                }}
                options={playerRulesEditModal.currentOption.automaticPool}
                disabled={loading}
                stretch
              />
            </FormField>
            <Box margin={{ top: "xs" }}></Box>
            {renderAutomaticComponent(automaticAction.value)}
          </Container>
        </SpaceBetween>
      </Box>
      <SpaceBetween direction="vertical" size="xxl">
        <Container header={<Box variant="h3">Rule Settings</Box>}>
          <SpaceBetween direction="vertical" size="m">
            {showColorPicker &&<Modal
              onDismiss={() => setShowColorPicker(false)}
              visible={showColorPicker}
              footer={
                <Box float="right">
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={() => setShowColorPicker(false)} variant="link">Cancel</Button>
                    <Button onClick={() => setShowColorPicker(false)} variant="primary">Ok</Button>
                  </SpaceBetween>
                </Box>
              }
              header="Rule color"
            >
              <ColorPicker color={color} onChange={setColor} />
              <Box margin={{top: "xs"}}></Box>
              <Container>
                <button
                  style={{ background: color.hex, outline: "none", border: 0, color: "white" }}>
                  Choose color
                </button>
              </Container>
            </Modal>}
            {showSecondaryColorPicker &&<Modal
              onDismiss={() => setShowSecondaryColorPicker(false)}
              visible={showSecondaryColorPicker}
              footer={
                <Box float="right">
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={() => setShowSecondaryColorPicker(false)} variant="link">Cancel</Button>
                    <Button onClick={() => setShowSecondaryColorPicker(false)} variant="primary">Ok</Button>
                  </SpaceBetween>
                </Box>
              }
              header="Secondary color"
            >
              <ColorPicker color={secondaryColor} onChange={setSecondaryColor} />
              <Box margin={{top: "xs"}}></Box>
              <Container>
                <button
                  style={{ background: secondaryColor.hex, outline: "none", border: 0, color: "white" }}>
                  Choose color
                </button>
              </Container>
            </Modal>}
            <FormField
              label="Rule Name"
              errorText={errors.ruleName}
              constraintText="Required"
              description="Even numbers"
              stretch
            >
              <Input
                value={ruleName}
                onChange={({ detail }) => updateRuleName(detail.value)}
                placeholder="Enter rule name"
                disabled={loading}
                stretch
              />
            </FormField>
            <FormField
              label="Secondary Name"
              stretch
              description="Odd numbers"
            >
              <Input
                value={secondaryName}
                onChange={({ detail }) => setSecondaryName(detail.value)}
                placeholder="Enter secondary name"
                disabled={loading}
                stretch
              />
            </FormField>
            <FormField
              label="Description"
              stretch
            >
              <Input
                value={description}
                onChange={({ detail }) => setDescription(detail.value)}
                placeholder="Enter description"
                disabled={loading}
                stretch
              />
            </FormField>
            <FormField
              label="Is External"
              constraintText="Required"
              stretch
            >
              <Toggle
                checked={external}
                onChange={(checkbox) => {
                  setExternal(checkbox.detail.checked);
                }}>
                External
              </Toggle>
            </FormField>
            <FormField
              label="Fixed risk tag"
              stretch
            >
              <Toggle
                checked={fixedTag}
                onChange={(checkbox) => {
                  setFixedTag(checkbox.detail.checked);
                }}>
                  {fixedTag ? "Yes" : "No"}
              </Toggle>
            </FormField>
            <FormField
              label="Rule Status"
              errorText={errors.enabledRule}
              constraintText="Required"
              stretch
            >
              <Select
                filteringType="auto"
                placeholder="Select status rule"
                selectedOption={enabledRule || null}
                onChange={({ detail }) => setEnabledRule(detail.selectedOption)}
                options={["ENABLED", "DISABLED"].map((e) => ({ value: e, label: e }))}
                disabled={loading}
                stretch
              />
            </FormField>
            <FormField
              label="Project"
              errorText={errors.brandList}
              constraintText="Required"
              stretch
            >
              <Multiselect
                selectedOptions={project}
                disabled={user.brandsList.length === 0}
                onChange={({ detail }) =>
                  setProject(detail.selectedOptions)
                }
                options={brandList}
                placeholder="Choose brand"
              />
            </FormField>
            <FormField
              label="Rule color"
              constraintText="Required"
              stretch
            >
              <SpaceBetween size={"m"} direction={"horizontal"}>
                <div
                  onClick={() => setShowColorPicker(true)}
                  style={{
                    width: "30px",
                    height: "30px",
                    backgroundColor: color.hex,
                    border: "1px solid #ccc",
                  }}
                />
              </SpaceBetween>
            </FormField>
            <FormField
              label="Secondary color"
              stretch
            >
              <SpaceBetween size={"m"} direction={"horizontal"}>
                <div
                  onClick={() => setShowSecondaryColorPicker(true)}
                  style={{
                    width: "30px",
                    height: "30px",
                    backgroundColor: secondaryColor.hex,
                    border: "1px solid #ccc",
                  }}
                />
              </SpaceBetween>
            </FormField>
            <Container header={<Box variant="h3">Condition Type</Box>}>
              <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
                <Select
                  filteringType="auto"
                  placeholder="Select condition type"
                  selectedOption={conditionType || null}
                  onChange={({ detail }) => updateConditionType(detail.selectedOption)}
                  options={conditionTypes}
                  disabled={loading}
                  stretch
                />
                <Button
                  iconName="add-plus"
                  onClick={addCondition}
                  disabled={!conditionType || loading}
                  variant="primary"
                  stretch
                >
                  Add Condition
                </Button>
              </Grid>
            </Container>
          </SpaceBetween>
        </Container>
        {conditions.map((condition, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <Box textAlign="center" padding={{ vertical: "xs" }}>
                <Select
                  selectedOption={{ value: "AND", label: "AND" }}
                  onChange={() => {}}
                  options={[{ value: "AND", label: "AND" }]}
                  disabled={true}
                />
                <span style={{ margin: "0 10px" }}>↓</span>
              </Box>
            )}
            <ConditionForm
              condition={condition}
              index={index}
              updateCondition={updateCondition}
              updateSourceField={updateSourceField}
              updateParam={updateParam}
              removeCondition={removeCondition}
              addSubCondition={addSubCondition}
              removeSubCondition={removeSubCondition}
              updateSubCondition={updateSubCondition}
              errors={errors}
              loading={loading}
              availableModules={formattedModules}
              loadFields={loadFields}
            />
          </React.Fragment>
        ))}
      </SpaceBetween>
    </Modal>
  );
};

export default withRecoil(PlayerRulesEditModal);

function withRecoil(Component) {
  return function WrappedComponent(props) {
    const playerRulesEditModal = useRecoilValue(playerRulesEditModalStore);
    const setPlayerRulesEditModal = useSetRecoilState(playerRulesEditModalStore);
    const user = useRecoilValue(userStore);
    return (
      <Component
        {...props}
        user={user}
        playerRulesEditModal={playerRulesEditModal}
        setPlayerRulesEditModal={setPlayerRulesEditModal}
      />
    );
  };
}
